// src/cockpit4/components/FollowUpAssistant.tsx
// ============================================
// P0-功能2：智能跟进助手（客户中心）
//
// 功能：
//   1. 今日待跟进客户列表（基于 followup.nextContact 判断）
//   2. 每个客户：建议跟进时间 + 跟进话术模板（按阶段生成不同话术）
//   3. 久未跟进自动提醒（7天/14天/30天三档）
//   4. 跟进记录快速录入（点击客户展开录入表单，写入 Vault via AppendHistory + MarkContacted）
//   5. 跟进效果统计（本周跟进次数、转化数）
//   6. 话术模板库（按阶段/场景内置模板）
//
// 数据来源：
//   - RuntimeManager.query(RuntimeName.CRM, { type: "list" }) 真实客户列表
//   - RuntimeManager.execute(RuntimeName.CRM, CRMCommand.AppendHistory, ...) 写入跟进记录
//   - RuntimeManager.execute(RuntimeName.CRM, CRMCommand.MarkContacted, ...) 更新联系日期
//   - 无数据时显示空状态提示，绝不展示假数据
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarClock,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Clock,
  MessageSquareText,
  ChevronDown,
  ChevronRight,
  Send,
  Sparkles,
  Activity,
  TrendingUp,
  Users,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import {
  CRMCommand,
  CUSTOMER_CATEGORY_CONFIG,
} from "../../runtime/CRMRuntime/types";
import type { CustomerProfile, CustomerStatus } from "../../runtime/CRMRuntime/types";
import type { FollowUpPlan } from "../../runtime/CRMRuntime/followup/types";
import {
  NotificationChannel,
  NotificationCommand,
} from "../../runtime/NotificationRuntime/types";

// ============================================
// 类型与常量
// ============================================

const DAY_MS = 24 * 60 * 60 * 1000;

/** 久未跟进档位 */
type StaleLevel = "stale_7" | "stale_14" | "stale_30";

interface StaleLevelMeta {
  id: StaleLevel;
  label: string;
  minDays: number;
  maxDays: number;
  color: string;
}

const STALE_LEVELS: StaleLevelMeta[] = [
  { id: "stale_7", label: "7天未跟进", minDays: 7, maxDays: 14, color: "#FBBF24" },
  { id: "stale_14", label: "14天未跟进", minDays: 14, maxDays: 30, color: "#FB923C" },
  { id: "stale_30", label: "30天未跟进", minDays: 30, maxDays: Number.MAX_SAFE_INTEGER, color: "#F87171" },
];

/** 客户跟进阶段（用于选择话术模板） */
type FollowupPhase = "lead" | "contact" | "requirement" | "negotiation" | "closed" | "reactivation";

/** 话术模板 */
interface ScriptTemplate {
  id: string;
  phase: FollowupPhase;
  scene: string;
  content: string;
}

/** 按阶段/场景内置的话术模板库 */
const SCRIPT_LIBRARY: ScriptTemplate[] = [
  {
    id: "lead-1",
    phase: "lead",
    scene: "初次接触",
    content:
      "您好，我是 XX 公司的 {owner}，了解到贵司在 {industry} 领域深耕多年，我们最近帮同类客户实现了 [核心价值]，想简单介绍一下，您看是否方便安排 15 分钟电话？",
  },
  {
    id: "lead-2",
    phase: "lead",
    scene: "加微信破冰",
    content:
      "{company} 的老师您好，刚才电话里提到的 [方案亮点] 我整理了一份资料，发您参考。如果方便的话，下周二/周四下午我们可以详细聊聊？",
  },
  {
    id: "contact-1",
    phase: "contact",
    scene: "需求挖掘",
    content:
      "上次沟通后我们梳理了贵司在 [业务场景] 的几个潜在痛点，想确认下目前最紧迫的是哪一块？我们可以针对性地准备一份方案大纲。",
  },
  {
    id: "requirement-1",
    phase: "requirement",
    scene: "方案确认",
    content:
      "根据之前沟通的需求，我们准备了一版初步方案，重点解决了 [关键问题]。需要我安排一次线下/视频会议，带技术同事一起过一遍细节吗？",
  },
  {
    id: "requirement-2",
    phase: "requirement",
    scene: "案例推送",
    content:
      "分享一个我们最近服务的 {industry} 行业案例 [案例名]，对方也遇到类似问题，最终 [量化效果]。供您参考，期待您的反馈。",
  },
  {
    id: "negotiation-1",
    phase: "negotiation",
    scene: "促成谈判",
    content:
      "关于合作方案，我们可以在 [商务条款] 上做一定让步，希望能在本月内推进签约。您这边还有哪些顾虑？我们可以一起对齐。",
  },
  {
    id: "negotiation-2",
    phase: "negotiation",
    scene: "临门一脚",
    content:
      "考虑到 {company} 的实施周期，建议本周内敲定合同，以便我们排期资源。如果今天能确认，我可以申请 [额外权益] 给到您。",
  },
  {
    id: "closed-1",
    phase: "closed",
    scene: "成交致谢",
    content:
      "感谢 {company} 的信任！合同已生效，我已拉通实施团队启动项目。本周内会安排项目启动会，期待合作愉快。",
  },
  {
    id: "closed-2",
    phase: "closed",
    scene: "售后回访",
    content:
      "项目上线已有一段时间，想回访一下使用情况。如果有任何问题或新需求，随时联系我，我们会优先支持。",
  },
  {
    id: "reactivation-1",
    phase: "reactivation",
    scene: "久未联系唤醒",
    content:
      "好久没联系了，最近 {industry} 行业有不少新变化，我们也有了新的 [产品/方案]。给您同步一下，您看是否有合作的新机会？",
  },
];

const PHASE_LABEL: Record<FollowupPhase, string> = {
  lead: "线索阶段",
  contact: "接触阶段",
  requirement: "需求阶段",
  negotiation: "谈判阶段",
  closed: "成交阶段",
  reactivation: "唤醒阶段",
};

const PHASE_COLOR: Record<FollowupPhase, string> = {
  lead: "#60A5FA",
  contact: "#38BDF8",
  requirement: "#A78BFA",
  negotiation: "#FB923C",
  closed: "#34D399",
  reactivation: "#F87171",
};

/** 跟进渠道中文名（FollowUpPlan.channel） */
const FOLLOWUP_CHANNEL_LABEL: Record<string, string> = {
  wechat: "微信",
  phone: "电话",
  email: "邮件",
  wecom: "企业微信",
  meeting: "上门会面",
  video: "视频会议",
};

/** 跟进目标中文名（FollowUpPlan.goal） */
const FOLLOWUP_GOAL_LABEL: Record<string, string> = {
  build_trust: "建立信任",
  qualify_need: "确认需求",
  present_solution: "方案展示",
  push_close: "促成成交",
  maintain: "关系维护",
};

// ============================================
// 触达执行配置（计划渠道 → 触达方式）
//   auto   ：通过 NotificationRuntime 真实发送（需已注册该渠道 + 配置 webhookUrl）
//   manual ：浏览器无法自动发送 → 生成人工执行待办（电话附拨号链接）
// ============================================

type TouchMode = "auto" | "manual";

interface TouchChannelDef {
  mode: TouchMode;
  label: string;
  /** auto：对应 NotificationRuntime 渠道 */
  notif?: NotificationChannel;
  /** manual：是否提供 tel: 拨号链接 */
  dial?: boolean;
}

const TOUCHPOINT_DEF: Record<string, TouchChannelDef> = {
  wecom: { mode: "auto", label: "企业微信", notif: NotificationChannel.WeChatWork },
  email: { mode: "auto", label: "邮件", notif: NotificationChannel.Email },
  wechat: { mode: "manual", label: "微信（个人号无开放 API，需人工）" },
  phone: { mode: "manual", label: "电话（人工外呼）", dial: true },
  meeting: { mode: "manual", label: "上门会面（人工）" },
  video: { mode: "manual", label: "视频会议（人工）" },
};

/** 指定渠道是否可真实自动发送 */
const isAutoChannel = (channel: string): boolean =>
  TOUCHPOINT_DEF[channel]?.mode === "auto";

// ============================================
// 工具函数
// ============================================

/** 计算距今天数（无效日期返回 -1） */
function daysSince(dateStr: string): number {
  if (!dateStr) return -1;
  const t = new Date(dateStr).getTime();
  if (isNaN(t)) return -1;
  return Math.floor((Date.now() - t) / DAY_MS);
}

/** YYYY-MM-DD */
function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

/** 当前周一起到周日 */
function getThisWeekRange(): { start: number; end: number } {
  const now = new Date();
  const day = now.getDay(); // 0=周日
  const monday = new Date(now);
  monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return { start: monday.getTime(), end: sunday.getTime() };
}

/**
 * 根据客户状态映射到跟进阶段（用于话术模板）
 * - prospecting 且无 lastContact → lead
 * - prospecting 且 lastContact ≤ 14 天 → contact
 * - prospecting 且 lastContact > 14 天 → requirement
 * - negotiating → negotiation
 * - closed-won / active → closed
 * - closed-lost → reactivation
 */
function mapPhase(c: CustomerProfile): FollowupPhase {
  const status: CustomerStatus | undefined = c.followup?.status;
  if (status === "closed-won" || status === "active") return "closed";
  if (status === "closed-lost") return "reactivation";
  if (status === "negotiating") return "negotiation";
  const d = daysSince(c.followup?.lastContact || "");
  if (d < 0) return "lead";
  if (d <= 14) return "contact";
  return "requirement";
}

/** 建议跟进时间：今日 14:00 / 16:00 / 次日 10:00 */
function suggestFollowupTime(c: CustomerProfile): string {
  const next = c.followup?.nextContact;
  if (next && !isNaN(new Date(next).getTime())) {
    const d = new Date(next);
    return `${d.getMonth() + 1}/${d.getDate()} 10:00`;
  }
  const now = new Date();
  const hour = now.getHours();
  if (hour < 14) return `今日 14:00`;
  if (hour < 16) return `今日 16:00`;
  return `明日 10:00`;
}

/** 替换话术模板变量 */
function renderTemplate(tpl: string, c: CustomerProfile): string {
  return tpl
    .replace(/\{company\}/g, c.company || "贵司")
    .replace(/\{owner\}/g, c.owner || "我")
    .replace(/\{industry\}/g, c.industry || "您所在行业");
}

/** 格式化金额 */
function formatAmount(amount: number): string {
  if (!amount || amount <= 0) return "—";
  if (amount >= 10000) return `¥${(amount / 10000).toFixed(1)}w`;
  return `¥${amount.toLocaleString()}`;
}

// ============================================
// 主组件
// ============================================

export default function FollowUpAssistant() {
  const [customers, setCustomers] = useState<CustomerProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 展开/折叠客户跟进录入表单（按客户 id）
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // 跟进记录录入文本（按客户 id 缓存）
  const [entryDrafts, setEntryDrafts] = useState<Record<string, string>>({});
  // 当前选中的话术模板（按客户 id）
  const [selectedTpl, setSelectedTpl] = useState<Record<string, ScriptTemplate>>({});
  // 提交状态
  const [submitting, setSubmitting] = useState<Record<string, boolean>>({});
  // 提交反馈
  const [submitMsg, setSubmitMsg] = useState<Record<string, { ok: boolean; text: string }>>({});

  // AI 自动跟进（手动触发，非定时）：批量生成状态、本次跟进条数
  const [aiRunning, setAiRunning] = useState(false);
  const [aiSingle, setAiSingle] = useState<Record<string, boolean>>({});
  const [aiLimit, setAiLimit] = useState(10);
  const [aiResults, setAiResults] = useState<
    Record<
      string,
      { ok: boolean; text: string; plan?: FollowUpPlan }
    >
  >({});

  // 触达执行（一键发送/生成人工待办）：单条状态 + 结果
  const [touchRunning, setTouchRunning] = useState<Record<string, boolean>>({});
  const [touchResults, setTouchResults] = useState<
    Record<string, { ok: boolean; text: string; dial?: string }>
  >({});

  // 加载真实客户列表
  const loadCustomers = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      for (let i = 0; i < 30; i++) {
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => setTimeout(r, 300));
      }
      if (!RuntimeManager.isInitialized()) {
        setError("RuntimeManager 尚未初始化");
        setLoading(false);
        return;
      }
      const resp = await RuntimeManager.query(RuntimeName.CRM, { type: "list" });
      if (resp?.success && resp.data) {
        const inner = resp.data as { data?: CustomerProfile[] } | CustomerProfile[];
        const list = Array.isArray(inner) ? inner : inner?.data || [];
        setCustomers(Array.isArray(list) ? list : []);
      } else {
        setError(resp?.error || "查询客户列表失败");
      }
    } catch (err) {
      setError((err as Error).message || "加载客户列表异常");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void loadCustomers();
  }, [loadCustomers]);

  // ============================================
  // 企业微信群机器人 Webhook 配置（真发依赖）
  // 从 localStorage 恢复并注册到 NotificationRuntime；保存即注册、session 内生效
  // ============================================
  const [wecomWebhook, setWecomWebhook] = useState<string>(
    () => localStorage.getItem("xeaos_wecom_webhook") || ""
  );

  useEffect(() => {
    if (!wecomWebhook) return;
    RuntimeManager.execute(RuntimeName.Notification, NotificationCommand.RegisterChannel, {
      channelConfig: {
        channel: NotificationChannel.WeChatWork,
        enabled: true,
        config: { webhookUrl: wecomWebhook },
      },
    })
      .then((r) => {
        if (!r.success) console.warn("[FollowUpAssistant] 注册企业微信渠道失败", r.error);
      })
      .catch((e) =>
        console.warn("[FollowUpAssistant] 注册企业微信渠道异常", e)
      );
  }, [wecomWebhook]);

  const saveWecomWebhook = useCallback((url: string) => {
    const v = url.trim();
    localStorage.setItem("xeaos_wecom_webhook", v);
    setWecomWebhook(v);
  }, []);

  // ============================================
  // 派生数据：今日待跟进 / 久未跟进 / 本周统计
  // ============================================

  const today = todayStr();

  const todayFollowups = useMemo(() => {
    return customers.filter((c) => {
      const next = c.followup?.nextContact;
      return next && next === today;
    });
  }, [customers, today]);

  const staleByLevel = useMemo(() => {
    const result: Record<StaleLevel, CustomerProfile[]> = {
      stale_7: [],
      stale_14: [],
      stale_30: [],
    };
    for (const c of customers) {
      const d = daysSince(c.followup?.lastContact || "");
      if (d < 7) continue;
      for (const lvl of STALE_LEVELS) {
        if (d >= lvl.minDays && d < lvl.maxDays) {
          result[lvl.id].push(c);
          break;
        }
      }
    }
    return result;
  }, [customers]);

  // 本周统计：跟进次数（history 中本周新增的条目数）、转化数（本周新成交）
  const weeklyStats = useMemo(() => {
    const { start, end } = getThisWeekRange();
    let followupCount = 0;
    let conversionCount = 0;

    for (const c of customers) {
      // 解析 history 中的 "- YYYY-MM-DD: ..." 条目
      if (c.history) {
        const matches = c.history.matchAll(/-\s*(\d{4}-\d{2}-\d{2})\s*:/g);
        for (const m of matches) {
          const t = new Date(m[1]).getTime();
          if (!isNaN(t) && t >= start && t <= end) {
            followupCount++;
          }
        }
      }
      // 转化：状态为 closed-won/active，且有 lastContact 在本周内
      const status = c.followup?.status;
      const lastT = new Date(c.followup?.lastContact || "").getTime();
      if (
        (status === "closed-won" || status === "active") &&
        !isNaN(lastT) &&
        lastT >= start &&
        lastT <= end
      ) {
        conversionCount++;
      }
    }
    return { followupCount, conversionCount };
  }, [customers]);

  // ============================================
  // 跟进记录快速录入：写入 Vault
  //   1. RuntimeManager.execute(CRM, AppendHistory, { id, entry })
  //   2. RuntimeManager.execute(CRM, MarkContacted, { id, date })
  // ============================================
  const submitFollowup = useCallback(
    async (customer: CustomerProfile) => {
      const entry = (entryDrafts[customer.id] || "").trim();
      if (!entry) return;
      setSubmitting((s) => ({ ...s, [customer.id]: true }));
      setSubmitMsg((s) => ({ ...s, [customer.id]: { ok: true, text: "提交中..." } }));
      try {
        if (!RuntimeManager.isInitialized()) {
          throw new Error("RuntimeManager 尚未初始化");
        }
        // 1. 追加沟通记录
        const appendResp = await RuntimeManager.execute(RuntimeName.CRM, CRMCommand.AppendHistory, {
          id: customer.id,
          entry,
        });
        const appendOk = appendResp?.success === true;
        if (!appendOk) {
          throw new Error(appendResp?.error || "追加沟通记录失败");
        }
        // 2. 更新最后联系日期为今天
        const markResp = await RuntimeManager.execute(RuntimeName.CRM, CRMCommand.MarkContacted, {
          id: customer.id,
          date: today,
        });
        if (markResp?.success !== true) {
          // 沟通记录已写入，仅标记失败 → 不视为致命错误
          console.warn("[FollowUpAssistant] MarkContacted 失败", markResp?.error);
        }

        setSubmitMsg((s) => ({
          ...s,
          [customer.id]: { ok: true, text: "✅ 已写入 Vault，并更新联系日期" },
        }));
        setEntryDrafts((s) => ({ ...s, [customer.id]: "" }));
        // 刷新列表，使"今日待跟进/久未跟进"实时反映
        void loadCustomers();
      } catch (err) {
        setSubmitMsg((s) => ({
          ...s,
          [customer.id]: { ok: false, text: `❌ ${(err as Error).message || "提交失败"}` },
        }));
      } finally {
        setSubmitting((s) => ({ ...s, [customer.id]: false }));
      }
    },
    [entryDrafts, today, loadCustomers]
  );

  // ============================================
  // AI 自动跟进（手动触发，非定时自动运行；为节省 token，整批限量）
  // 优先级池：今日待跟进 → 久未跟进(7/14/30 天) → 其余客户
  // 逐条调用 CRMCommand.PlanFollowUp → 引擎智能生成话术（LLM 不可用时降级模板）并写入 Vault
  // ============================================

  /** 待跟进队列（按优先级排序的客户 id，不截断，由 aiLimit 在运行时截取） */
  const followupQueue = useMemo(() => {
    const order: CustomerProfile[] = [];
    const seen = new Set<string>();
    const push = (list: CustomerProfile[]) => {
      for (const c of list) {
        if (!seen.has(c.id)) {
          seen.add(c.id);
          order.push(c);
        }
      }
    };
    push(todayFollowups);
    push(staleByLevel.stale_7);
    push(staleByLevel.stale_14);
    push(staleByLevel.stale_30);
    // 补足尚未排入优先级的其余客户
    for (const c of customers) {
      if (!seen.has(c.id)) {
        seen.add(c.id);
        order.push(c);
      }
    }
    return order;
  }, [customers, todayFollowups, staleByLevel]);

  /** 单个客户：AI 生成跟进话术 */
  const runSingleFollowup = useCallback(async (c: CustomerProfile) => {
    setAiSingle((s) => ({ ...s, [c.id]: true }));
    setAiResults((s) => ({ ...s, [c.id]: { ok: true, text: "AI 生成中..." } }));
    try {
      if (!RuntimeManager.isInitialized()) {
        throw new Error("RuntimeManager 尚未初始化");
      }
      const resp = await RuntimeManager.execute(RuntimeName.CRM, CRMCommand.PlanFollowUp, {
        customerId: c.id,
      });
      const data = resp?.data as
        | { success?: boolean; plan?: FollowUpPlan; error?: string; updated?: boolean }
        | undefined;
      if (resp?.success && data?.plan) {
        const p = data.plan;
        setAiResults((s) => ({
          ...s,
          [c.id]: {
            ok: true,
            text: `已生成 · ${FOLLOWUP_GOAL_LABEL[p.goal] || p.goal} · ${
              FOLLOWUP_CHANNEL_LABEL[p.channel] || p.channel
            } · ${p.followUpDate}${data.updated ? " · 已写入 Vault" : ""} · 生成方式 ${
              p.generatedBy === "llm" ? "AI" : "模板"
            }`,
            plan: p,
          },
        }));
      } else {
        setAiResults((s) => ({
          ...s,
          [c.id]: { ok: false, text: data?.error || resp?.error || "AI 生成失败" },
        }));
      }
    } catch (err) {
      setAiResults((s) => ({
        ...s,
        [c.id]: { ok: false, text: `❌ ${(err as Error).message || "AI 生成异常"}` },
      }));
    } finally {
      setAiSingle((s) => ({ ...s, [c.id]: false }));
    }
  }, []);

  /** 批量：按 aiLimit 截取队列，逐个 AI 生成话术 */
  const runBatchFollowup = useCallback(async () => {
    const queue = followupQueue.slice(0, aiLimit);
    if (queue.length === 0) return;
    if (!RuntimeManager.isInitialized()) {
      setError("RuntimeManager 尚未初始化");
      return;
    }
    setAiRunning(true);
    setAiResults({});
    for (const c of queue) {
      setAiSingle((s) => ({ ...s, [c.id]: true }));
      try {
        const resp = await RuntimeManager.execute(RuntimeName.CRM, CRMCommand.PlanFollowUp, {
          customerId: c.id,
        });
        const data = resp?.data as
          | { success?: boolean; plan?: FollowUpPlan; error?: string; updated?: boolean }
          | undefined;
        if (resp?.success && data?.plan) {
          const p = data.plan;
          setAiResults((s) => ({
            ...s,
            [c.id]: {
              ok: true,
              text: `已生成 · ${FOLLOWUP_GOAL_LABEL[p.goal] || p.goal} · ${
                FOLLOWUP_CHANNEL_LABEL[p.channel] || p.channel
              }${data.updated ? " · 已写入 Vault" : ""}${
                p.generatedBy === "rule-based" ? " · 模板 (LLM 不可用)" : ""
              }`,
              plan: p,
            },
          }));
        } else {
          setAiResults((s) => ({
            ...s,
            [c.id]: { ok: false, text: data?.error || resp?.error || "AI 生成失败" },
          }));
        }
      } catch (err) {
        setAiResults((s) => ({
          ...s,
          [c.id]: { ok: false, text: `❌ ${(err as Error).message || "AI 生成异常"}` },
        }));
      } finally {
        setAiSingle((s) => ({ ...s, [c.id]: false }));
      }
    }
    setAiRunning(false);
    // 计划可能写入了客户档案（下一步建议），刷新最新状态
    void loadCustomers();
  }, [followupQueue, aiLimit, loadCustomers]);

  // ============================================
  // 触达执行（真实发送 / 生成人工待办）
  //   auto 渠道（企业微信/邮件）→ 通过 NotificationRuntime 真实发送，发成功即归档历史 + 更新联系日期
  //   manual 渠道（电话/微信个人号/上门/视频）→ 浏览器无法自动发送，生成人工待办，电话附拨号链接
  // ============================================

  /** 自动触达成功后，归档到客户档案（追加历史 + 更新联系日期） */
  const recordAutoTouch = useCallback(
    async (customer: CustomerProfile, label: string, script: string) => {
      if (!RuntimeManager.isInitialized()) return;
      try {
        await RuntimeManager.execute(RuntimeName.CRM, CRMCommand.AppendHistory, {
          id: customer.id,
          entry: `通过 ${label} 自动触达：${script}`,
        });
        await RuntimeManager.execute(RuntimeName.CRM, CRMCommand.MarkContacted, {
          id: customer.id,
          date: today,
        });
      } catch (err) {
        console.warn("[FollowUpAssistant] 自动触达归档失败（非阻塞）", err);
      }
    },
    [today]
  );

  /** 一键触达单个客户：auto 真发，manual 生成人工待办 */
  const execTouchpoint = useCallback(
    async (cust: CustomerProfile, plan: FollowUpPlan, forcedChannel?: string) => {
      const channel = forcedChannel || plan.channel;
      const def = TOUCHPOINT_DEF[channel] || { mode: "manual" as TouchMode, label: channel };
      setTouchRunning((s) => ({ ...s, [cust.id]: true }));
      setTouchResults((s) => ({ ...s, [cust.id]: { ok: true, text: "处理中..." } }));
      try {
        if (def.mode === "auto" && def.notif) {
          if (!RuntimeManager.isInitialized()) {
            throw new Error("RuntimeManager 尚未初始化");
          }
          // 解析收件人（企业微信群机器人/邮件使用客户联系方式）
          const rec: string[] = [];
          if (def.notif === NotificationChannel.Email && cust.contact?.email)
            rec.push(cust.contact.email);
          if (def.notif === NotificationChannel.WeChatWork && cust.contact?.wechat)
            rec.push(cust.contact.wechat);
          const resp = await RuntimeManager.execute(
            RuntimeName.Notification,
            NotificationCommand.Send,
            {
              channel: def.notif,
              message: {
                title: `跟进 · ${cust.company}`,
                content: plan.script,
                contentType: "markdown",
                recipients: rec,
                priority: "normal",
                metadata: {
                  source: "followup-assistant",
                  customerId: cust.id,
                  channel,
                },
              },
            }
          );
          const sendResult = resp?.data as
            | { success?: boolean; error?: string; messageId?: string }
            | undefined;
          if (!resp?.success || !sendResult?.success) {
            setTouchResults((s) => ({
              ...s,
              [cust.id]: {
                ok: false,
                text: `❌ ${sendResult?.error || resp?.error || "发送失败"}`,
              },
            }));
            return;
          }
          // 发送成功：归档历史 + 更新联系日期
          await recordAutoTouch(cust, def.label, plan.script);
          setTouchResults((s) => ({
            ...s,
            [cust.id]: {
              ok: true,
              text: `✅ 已通过${def.label}发送成功，已写入跟进记录`,
            },
          }));
          return;
        }

        // 人工渠道：生成待办 + 可选拨号链接（浏览器无法自动外呼）
        setTouchResults((s) => ({
          ...s,
          [cust.id]: {
            ok: true,
            text: `👤 已生成「${def.label}」人工待办（该渠道无法由浏览器自动发送）`,
            dial: def.dial ? cust.contact?.phone : undefined,
          },
        }));
      } catch (err) {
        const m = err instanceof Error ? err.message : String(err);
        setTouchResults((s) => ({ ...s, [cust.id]: { ok: false, text: `❌ ${m}` } }));
      } finally {
        setTouchRunning((s) => ({ ...s, [cust.id]: false }));
        void loadCustomers();
      }
    },
    [recordAutoTouch, loadCustomers]
  );

  /** 批量触达：对已生成计划的客户逐个一键触达（真发 auto，生成待办 for manual） */
  const runBatchTouch = useCallback(async () => {
    const entries = Object.entries(aiResults);
    const targets = entries.filter(([, r]) => r.plan) as [
      string,
      { ok: boolean; text: string; plan: FollowUpPlan }
    ][];
    if (targets.length === 0) return;
    for (const [id, r] of targets) {
      const cust = customers.find((c) => c.id === id);
      if (!cust) continue;
      await execTouchpoint(cust, r.plan);
    }
  }, [aiResults, customers, execTouchpoint]);

  // ============================================
  // 渲染辅助
  // ============================================

  /** 已生成计划数（用于批量触达按钮） */
  const planCount = Object.values(aiResults).filter((r) => r.plan).length;
  /** 是否有触达正在执行 */
  const anyTouchRunning = Object.values(touchRunning).some(Boolean);

  const renderCustomerRow = (c: CustomerProfile, idx: number, hint?: { label: string; color: string }) => {
    const phase = mapPhase(c);
    const isExpanded = expandedId === c.id;
    const draft = entryDrafts[c.id] || "";
    const tpl = selectedTpl[c.id];
    const isSubmitting = submitting[c.id] === true;
    const msg = submitMsg[c.id];
    const aiResult = aiResults[c.id];
    const touchResult = touchResults[c.id];
    const isTouchRunning = touchRunning[c.id] === true;
    const catConfig = CUSTOMER_CATEGORY_CONFIG[c.category] || CUSTOMER_CATEGORY_CONFIG.other;
    const phaseTemplates = SCRIPT_LIBRARY.filter((t) => t.phase === phase);

    return (
      <div
        key={c.id}
        className="rounded-md border border-white/[0.04] bg-white/[0.02] overflow-hidden"
      >
        <button
          onClick={() => setExpandedId(isExpanded ? null : c.id)}
          className="w-full flex items-center gap-2 p-2.5 text-left hover:bg-white/[0.03] transition-colors"
        >
          <span className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center text-[10px] font-mono text-gray-400 bg-white/[0.04]">
            {idx + 1}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-gray-200 truncate">{c.company}</span>
              <span
                className="w-1 h-1 rounded-full flex-shrink-0"
                style={{ background: catConfig.color }}
              />
              <span
                className="px-1 rounded text-[8px] font-mono"
                style={{ background: `${phase === "reactivation" ? "#F8717140" : PHASE_COLOR[phase] + "20"}`, color: PHASE_COLOR[phase] }}
              >
                {PHASE_LABEL[phase]}
              </span>
              {hint && (
                <span
                  className="px-1 rounded text-[8px] font-mono"
                  style={{ background: `${hint.color}20`, color: hint.color }}
                >
                  {hint.label}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5 text-[9px] font-mono text-gray-600">
              <span>{c.owner}</span>
              <span>·</span>
              <span>{c.industry || "—"}</span>
              <span>·</span>
              <span className="text-emerald-400/80">{formatAmount(c.followup?.estimatedValue || 0)}</span>
            </div>
          </div>
          <div className="flex-shrink-0 text-right">
            <div className="flex items-center gap-1 text-[9px] font-mono text-amber-400">
              <Clock size={9} />
              {suggestFollowupTime(c)}
            </div>
            <div className="text-[9px] font-mono text-gray-600 mt-0.5">
              上次：{c.followup?.lastContact || "—"}
            </div>
          </div>
          {isExpanded ? (
            <ChevronDown size={11} className="text-gray-500 flex-shrink-0" />
          ) : (
            <ChevronRight size={11} className="text-gray-500 flex-shrink-0" />
          )}
        </button>

        {/* 展开内容：话术模板 + 录入表单 */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="border-t border-white/[0.04] overflow-hidden"
            >
              <div className="p-2.5 space-y-2">
                {/* AI 生成跟进话术 */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <Sparkles size={10} className="text-fuchsia-400" />
                      <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                        AI 生成跟进话术
                      </span>
                    </div>
                    <button
                      onClick={() => void runSingleFollowup(c)}
                      disabled={aiSingle[c.id] === true}
                      className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-fuchsia-500/15 border border-fuchsia-500/30 text-fuchsia-300 hover:bg-fuchsia-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {aiSingle[c.id] === true ? (
                        <Loader2 size={9} className="animate-spin" />
                      ) : (
                        <Sparkles size={9} />
                      )}
                      {aiSingle[c.id] === true ? "生成中..." : "AI 话术"}
                    </button>
                  </div>
                  {aiResult && (
                    <div
                      className={`px-2 py-1.5 rounded text-[9px] font-mono border leading-snug ${
                        aiResult.ok
                          ? "border-fuchsia-500/30 bg-fuchsia-500/[0.06] text-fuchsia-200"
                          : "border-red-500/30 bg-red-500/[0.06] text-red-300"
                      }`}
                    >
                      {aiResult.text}
                      {aiResult.plan?.script && (
                        <div className="mt-1.5 pt-1.5 border-t border-white/[0.06] whitespace-pre-wrap text-gray-400">
                          {aiResult.plan.script}
                          {aiResult.plan.fallbackScript && (
                            <>
                              <div className="mt-1 text-gray-600">备选话术（客户回应冷淡时）：</div>
                              <div className="text-gray-500">{aiResult.plan.fallbackScript}</div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* 一键触达（真发 / 生成人工待办） */}
                  {aiResult?.plan && (
                    <div className="pt-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <button
                          onClick={() => void execTouchpoint(c, aiResult.plan!)}
                          disabled={isTouchRunning}
                          className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[9px] font-mono uppercase tracking-wider bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isTouchRunning ? (
                            <Loader2 size={9} className="animate-spin" />
                          ) : (
                            <Send size={9} />
                          )}
                          {isTouchRunning
                            ? "触达中..."
                            : isAutoChannel(aiResult.plan.channel)
                              ? "一键发送触达"
                              : "一键触达（人工）"}
                        </button>
                        {/* 人工渠道可改发企业微信群（唯一零后端真发通道） */}
                        {!isAutoChannel(aiResult.plan.channel) && (
                          <button
                            onClick={() => void execTouchpoint(c, aiResult.plan!, "wecom")}
                            disabled={isTouchRunning}
                            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-sky-500/10 border border-sky-500/30 text-sky-300 hover:bg-sky-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <MessageSquareText size={8} />
                            改为企微群发送
                          </button>
                        )}
                        <span className="text-[9px] font-mono text-gray-600">
                          渠道：{FOLLOWUP_CHANNEL_LABEL[aiResult.plan.channel] || aiResult.plan.channel}
                        </span>
                      </div>

                      {/* 触达结果 / 人工待办 + 拨号链接 */}
                      {touchResult && (
                        <div
                          className={`mt-1.5 px-2 py-1.5 rounded text-[9px] font-mono border leading-snug ${
                            touchResult.ok
                              ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-200"
                              : "border-red-500/30 bg-red-500/[0.06] text-red-300"
                          }`}
                        >
                          {touchResult.text}
                          {touchResult.dial && (
                            <div className="mt-1.5 flex items-center gap-2">
                              <span className="text-gray-500">客户电话：{touchResult.dial || "—"}</span>
                              <a
                                href={`tel:${touchResult.dial}`}
                                className="px-1.5 py-0.5 rounded bg-sky-500/15 border border-sky-500/40 text-sky-300 hover:bg-sky-500/25"
                              >
                                📞 拨打
                              </a>
                              <span className="text-gray-600">
                                拨打后请在下方录入跟进内容以完成归档
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* 话术模板库 */}
                <div>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <MessageSquareText size={10} className="text-cyan-400" />
                    <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                      话术模板（{PHASE_LABEL[phase]}）
                    </span>
                  </div>
                  {phaseTemplates.length === 0 ? (
                    <p className="text-[10px] font-mono text-gray-600">该阶段暂无内置模板</p>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {phaseTemplates.map((t) => (
                        <button
                          key={t.id}
                          onClick={() => {
                            setSelectedTpl((s) => ({ ...s, [c.id]: t }));
                            setEntryDrafts((s) => ({ ...s, [c.id]: renderTemplate(t.content, c) }));
                          }}
                          className={`px-1.5 py-0.5 rounded text-[9px] font-mono border transition-colors ${
                            tpl?.id === t.id
                              ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300"
                              : "bg-white/[0.02] border-white/[0.06] text-gray-400 hover:bg-white/[0.05]"
                          }`}
                        >
                          {t.scene}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* 录入表单 */}
                <div>
                  <textarea
                    value={draft}
                    onChange={(e) =>
                      setEntryDrafts((s) => ({ ...s, [c.id]: e.target.value }))
                    }
                    placeholder="录入本次跟进记录（如：电话沟通 15 分钟，确认了预算范围与决策人...），提交后将写入 Vault 并更新联系日期"
                    rows={3}
                    className="w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono resize-none focus:outline-none focus:border-cyan-500/40"
                  />
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-[9px] font-mono text-gray-600">
                      {draft.length} 字
                    </span>
                    <button
                      onClick={() => void submitFollowup(c)}
                      disabled={!draft.trim() || isSubmitting}
                      className="flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-mono uppercase tracking-wider bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <Loader2 size={10} className="animate-spin" />
                      ) : (
                        <Send size={10} />
                      )}
                      {isSubmitting ? "提交中..." : "提交跟进"}
                    </button>
                  </div>
                </div>

                {/* 提交反馈 */}
                {msg && (
                  <div
                    className={`px-2 py-1 rounded text-[9px] font-mono border ${
                      msg.ok
                        ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300"
                        : "border-red-500/30 bg-red-500/[0.06] text-red-300"
                    }`}
                  >
                    {msg.text}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-emerald-500/15 border border-emerald-500/30">
            <CalendarClock size={13} className="text-emerald-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">智能跟进助手</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Follow-up Assistant · 今日 {todayFollowups.length} 待跟进 · 本周 {weeklyStats.followupCount} 次
            </p>
          </div>
        </div>
        <button
          onClick={loadCustomers}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors disabled:opacity-50"
        >
          {refreshing ? (
            <Loader2 size={11} className="text-emerald-400 animate-spin" />
          ) : (
            <RefreshCw size={11} className="text-emerald-400" />
          )}
          <span className="text-[9px] font-mono text-emerald-300 uppercase tracking-wider">
            {refreshing ? "刷新中..." : "刷新"}
          </span>
        </button>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div className="px-3 py-2 rounded-md border border-red-500/30 bg-red-500/[0.06] text-red-300 text-[10px] font-mono">
            {error}
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center">
          <Loader2 size={20} className="mx-auto text-emerald-400 animate-spin" />
          <p className="text-[10px] font-mono text-gray-600 mt-2">加载跟进数据...</p>
        </div>
      ) : customers.length === 0 ? (
        <div className="py-10 text-center">
          <Users size={24} className="mx-auto text-gray-600 mb-2" />
          <p className="text-[11px] font-mono text-gray-500">暂无客户数据</p>
          <p className="text-[9px] font-mono text-gray-700 mt-1">
            数据来源：RuntimeManager.query(CRM, {`{ type: "list" }`})
          </p>
        </div>
      ) : (
        <>
          {/* 跟进效果统计 */}
          <div className="grid grid-cols-3 gap-2 p-4 border-b border-white/[0.04]">
            <StatCard
              icon={Activity}
              label="本周跟进次数"
              value={String(weeklyStats.followupCount)}
              color="#3B82F6"
            />
            <StatCard
              icon={TrendingUp}
              label="本周转化数"
              value={String(weeklyStats.conversionCount)}
              color="#34D399"
            />
            <StatCard
              icon={AlertTriangle}
              label="久未跟进总数"
              value={String(
                staleByLevel.stale_7.length +
                  staleByLevel.stale_14.length +
                  staleByLevel.stale_30.length
              )}
              color="#F87171"
            />
          </div>

          {/* AI 自动跟进（手动触发，非定时） */}
          <div className="p-4 border-b border-white/[0.04]">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={11} className="text-fuchsia-400" />
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                AI 自动跟进（手动触发）
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => void runBatchFollowup()}
                disabled={aiRunning || customers.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gradient-to-r from-fuchsia-500/20 to-violet-500/20 border border-fuchsia-500/40 text-fuchsia-200 hover:from-fuchsia-500/30 hover:to-violet-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {aiRunning ? (
                  <Loader2 size={11} className="animate-spin" />
                ) : (
                  <Sparkles size={11} />
                )}
                {aiRunning ? "AI 跟进中..." : "AI 批量跟进"}
              </button>
              <div className="flex items-center gap-1 text-[9px] font-mono text-gray-500">
                <span>本次</span>
                <input
                  type="number"
                  min={1}
                  max={Math.max(1, customers.length)}
                  value={aiLimit}
                  onChange={(e) =>
                    setAiLimit(Math.max(1, Math.min(50, Number(e.target.value) || 1)))
                  }
                  className="w-12 px-1 py-0.5 rounded bg-white/[0.03] border border-white/[0.08] text-[9px] font-mono text-gray-300 text-center focus:outline-none focus:border-fuchsia-500/40"
                />
                <span>位客户</span>
              </div>
              <button
                onClick={() => void runBatchTouch()}
                disabled={anyTouchRunning || planCount === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-500/10 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={9} />
                批量触达（{planCount}）
              </button>
              <span className="text-[9px] font-mono text-gray-600 ml-auto">
                {aiRunning
                  ? "处理中，请勿重复点击"
                  : `本次最多 ${aiLimit} 次 AI 调用 · 优先处理今日待跟进与久未跟进客户 · 不占后台，省 token`}
              </span>
            </div>

            {/* 企业微信群机器人 Webhook 配置（真发依赖，保存即注册） */}
            <div className="mt-2 pt-2 border-t border-white/[0.05] flex flex-wrap items-center gap-2">
              <span className="text-[9px] font-mono text-gray-500 flex-shrink-0">
                企微群机器人 Webhook：
              </span>
              <input
                value={wecomWebhook}
                onChange={(e) => saveWecomWebhook(e.target.value)}
                placeholder="https://qyapi.weixin.qq.com/cgi-bin/webhook/send?key=xxxx"
                className="flex-1 min-w-[200px] px-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.08] text-[9px] font-mono text-gray-300 focus:outline-none focus:border-sky-500/40 placeholder:text-gray-700"
              />
              <span
                className={`flex items-center gap-1 text-[9px] font-mono ${
                  wecomWebhook ? "text-emerald-400" : "text-gray-600"
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    wecomWebhook ? "bg-emerald-400" : "bg-gray-600"
                  }`}
                />
                {wecomWebhook ? "已注册，企微渠道可真发" : "未配置，企微发送将失败"}
              </span>
            </div>

            {/* 批量结果 */}
            {Object.keys(aiResults).length > 0 && !aiRunning && (
              <div className="mt-2 space-y-1 max-h-[200px] overflow-y-auto pr-1">
                {Object.entries(aiResults).map(([id, r]) => {
                  const c = customers.find((x) => x.id === id);
                  return (
                    <div
                      key={id}
                      className={`px-2 py-1.5 rounded border text-[9px] font-mono leading-snug ${
                        r.ok
                          ? "border-fuchsia-500/25 bg-fuchsia-500/[0.05] text-fuchsia-200"
                          : "border-red-500/25 bg-red-500/[0.05] text-red-300"
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: r.ok ? "#E879F9" : "#F87171" }} />
                        <span className="text-gray-200 truncate">{c?.company || id}</span>
                        <span className="text-fuchsia-400/80 ml-auto flex-shrink-0">{r.text}</span>
                      </div>
                      {r.plan?.script && (
                        <div className="mt-1 pl-3 whitespace-pre-wrap text-gray-500">
                          {r.plan.script}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 今日待跟进 */}
          <div className="p-4 border-b border-white/[0.04]">
            <div className="flex items-center gap-2 mb-2">
              <CalendarClock size={11} className="text-emerald-400" />
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                今日待跟进（{todayFollowups.length}）
              </span>
            </div>
            {todayFollowups.length === 0 ? (
              <p className="text-[11px] font-mono text-gray-600 text-center py-3">
                今日无待跟进客户（followup.nextContact ≠ {today}）
              </p>
            ) : (
              <div className="space-y-1.5">
                {todayFollowups.map((c, i) => renderCustomerRow(c, i))}
              </div>
            )}
          </div>

          {/* 久未跟进提醒（三档） */}
          {STALE_LEVELS.map((lvl) => {
            const list = staleByLevel[lvl.id];
            return (
              <div key={lvl.id} className="p-4 border-b border-white/[0.04]">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={11} style={{ color: lvl.color }} />
                  <span
                    className="text-[10px] font-mono uppercase tracking-wider"
                    style={{ color: lvl.color }}
                  >
                    {lvl.label}（{list.length}）
                  </span>
                </div>
                {list.length === 0 ? (
                  <p className="text-[11px] font-mono text-gray-600 text-center py-2">
                    暂无该档客户
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {list.map((c, i) =>
                      renderCustomerRow(c, i, {
                        label: `${daysSince(c.followup?.lastContact || "")}天`,
                        color: lvl.color,
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* 话术模板库（全部） */}
          <div className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquareText size={11} className="text-cyan-400" />
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                话术模板库（按阶段）
              </span>
            </div>
            <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
              {(Object.keys(PHASE_LABEL) as FollowupPhase[]).map((phase) => {
                const tpls = SCRIPT_LIBRARY.filter((t) => t.phase === phase);
                if (tpls.length === 0) return null;
                return (
                  <div key={phase} className="rounded-md border border-white/[0.04] bg-white/[0.01] p-2">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ background: PHASE_COLOR[phase] }}
                      />
                      <span
                        className="text-[10px] font-mono uppercase tracking-wider"
                        style={{ color: PHASE_COLOR[phase] }}
                      >
                        {PHASE_LABEL[phase]}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {tpls.map((t) => (
                        <div
                          key={t.id}
                          className="rounded bg-white/[0.02] border border-white/[0.04] p-1.5"
                        >
                          <div className="text-[10px] text-gray-300 mb-0.5">{t.scene}</div>
                          <div className="text-[9px] font-mono text-gray-500 leading-snug">
                            {t.content}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
}

// ============================================
// 统计小卡片
// ============================================
interface StatCardProps {
  icon: typeof Activity;
  label: string;
  value: string;
  color: string;
}

function StatCard({ icon: Icon, label, value, color }: StatCardProps) {
  return (
    <div
      className="rounded-md border border-white/[0.06] bg-white/[0.02] px-2.5 py-1.5 flex items-center gap-2"
      style={{ borderLeft: `2px solid ${color}` }}
    >
      <Icon size={13} style={{ color }} className="flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider truncate">
          {label}
        </div>
        <div className="text-[14px] font-mono font-bold" style={{ color }}>
          {value}
        </div>
      </div>
    </div>
  );
}
